// src/pages/MusicListScreen.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { initDynamicFirebase } from "../constants/initDynamicFirebase";
// import Sidebar from "../components/Sidebar"; // REMOVE
import Footerbar from "../components/Footerbar"; // ADD
import Navbar from "../components/Navbar"; // ADD
import { PlayCircle, UserCircle, ListMusic, AlertTriangle, Star } from 'lucide-react'; // Import Icons

// --- Helper: Inject CSS Keyframes for Spinners (if needed for loading state) ---
if (!document.getElementById('spinner-keyframes')) {
    const keyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.id = 'spinner-keyframes';
    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
}
// --- End Helper ---


export default function MusicListScreen() {
    const [musics, setMusics] = useState([]);
    // const [currentPlaying, setCurrentPlaying] = useState(null); // Not strictly needed if isPlaying is in music object
    // const [isSidebarOpen, setIsSidebarOpen] = useState(false); // REMOVE
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // Add error state
    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true; // Flag to prevent state updates on unmounted component
        let unsubscribe = null; // Variable to hold the unsubscribe function

        const fetchUserAndMusic = async () => {
            setLoading(true);
            setError(null);
            setMusics([]); // Clear previous music
            console.log("MusicListScreen: Fetching user data...");

            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    console.log("MusicListScreen: No token found, redirecting to login.");
                    if (isMounted) navigate('/');
                    return;
                }

                const response = await axios.get("https://apem.riderup.uk/api/user", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const user = response.data;
                if (!isMounted) return; // Exit if component unmounted during fetch
                setUserData(user);
                console.log("MusicListScreen: User data fetched:", user?.id);

                const bar = user?.client;
                if (!bar) {
                    console.warn("MusicListScreen: User not connected to a bar.");
                    setError("Você não está conectado a um estabelecimento.");
                    setLoading(false);
                    return; // Stop if no bar connection
                }
                console.log("MusicListScreen: User connected to bar:", bar.name);

                // --- Firebase Setup ---
                console.log("MusicListScreen: Setting up Firebase...");
                const config = {
                    apiKey: "AIzaSyBMVTvCciOYjyTFES0wapwkiVxTRL_9WJU",
                    authDomain: "apemall.firebaseapp.com",
                    projectId: "apemall",
                    storageBucket: "apemall.firebasestorage.app",
                    messagingSenderId: "824620532506",
                    appId: "1:824620532506:web:1378444a5c52e09807e2a3",
                    measurementId: "G-1YDCLNVCKH"
                };
                if (!config.apiKey || !config.projectId) {
                    throw new Error("Configuração do Firebase do bar está incompleta.");
                }

                const db = await initDynamicFirebase(config);
                const empresaId = String(bar.id);
                const musicRef = collection(db, 'empresa', empresaId, 'lista');
                const q = query(musicRef, orderBy("fecha", "asc"));
                console.log("MusicListScreen: Firebase query created. Setting up listener...");

                // --- Firestore Listener ---
                unsubscribe = onSnapshot(q, (querySnapshot) => {
                    console.log("MusicListScreen: Firestore snapshot received.");
                    const musicData = querySnapshot.docs.map((doc, index) => {
                        const data = doc.data();
                        // Basic validation/defaults
                        const id = data.id || doc.id; // Use Firestore doc id as fallback maybe? Or ensure data.id exists
                        const title = data.title || "Título Desconhecido";
                        const author = data.author || "Artista Desconhecido";
                        const userId = data.user_id;

                        return {
                            key: doc.id, // Use Firestore doc id as key
                            id: id,
                            title: title,
                            author: author,
                            user_id: userId,
                            // Use mqdefault for smaller thumbnail, better performance
                            thumbnail: `https://i.ytimg.com/vi/${id}/mqdefault.jpg`,
                            isPlaying: index === 0, // First item is always playing
                        };
                    });

                    if (isMounted) {
                        console.log("MusicListScreen: Updating music state with", musicData.length, "items.");
                        setMusics(musicData);
                        // setCurrentPlaying(musicData[0] || null); // Update currentPlaying if needed elsewhere
                        setLoading(false); // Stop loading *after* first data received
                        setError(null); // Clear any previous error on successful update
                    }

                }, (err) => { // Error handling for the listener itself
                    console.error("MusicListScreen: Error in Firestore listener:", err);
                    if (isMounted) {
                        setError("Erro ao carregar a lista de músicas em tempo real.");
                        setLoading(false);
                        setMusics([]); // Clear music on listener error
                    }
                });
                console.log("MusicListScreen: Firestore listener attached.");

            } catch (err) {
                console.error("MusicListScreen: Error fetching user or setting up Firebase:", err);
                if (isMounted) {
                    // Handle specific errors like 401 Unauthorized
                    if (axios.isAxiosError(err) && err.response?.status === 401) {
                        localStorage.removeItem("token");
                        navigate('/'); // Redirect to login on 401
                    } else {
                        setError(err.message || "Ocorreu um erro inesperado.");
                        setLoading(false);
                    }
                }
            }
        };

        fetchUserAndMusic();

        // Cleanup function: Will be called when the component unmounts
        return () => {
            isMounted = false; // Set flag
            if (unsubscribe) {
                console.log("MusicListScreen: Unsubscribing from Firestore listener.");
                unsubscribe(); // Detach the listener
            }
        };
    }, [navigate]); // Dependency: navigate

    // --- Render Function for Each Music Item ---
    const renderMusicItem = (music) => {
        // Check if the music was requested by the current logged-in user
        const isMine = userData && music.user_id === userData.id;

        return (
            // Use the key prop here, derived from Firestore doc id
            <div key={music.key} style={styles.musicCard(music.isPlaying)}>
                <img
                    src={music.thumbnail}
                    alt={`Thumbnail for ${music.title}`}
                    style={styles.thumbnail}
                    onError={(e) => { e.target.style.display = 'none'; }} // Hide broken images
                />
                <div style={styles.musicInfo}>
                    {/* Badges Area */}
                    <div style={styles.badgesContainer}>
                        {music.isPlaying && (
                            <span style={{ ...styles.badge, ...styles.playingBadge }}>
                                <PlayCircle size={12} style={{ marginRight: '3px' }} /> TOCANDO
                            </span>
                        )}
                        {isMine && (
                            <span style={{ ...styles.badge, ...styles.myMusicBadge }}>
                                <Star size={12} style={{ marginRight: '3px' }} /> SUA MÚSICA
                            </span>
                        )}
                    </div>
                    {/* Text Info */}
                    <h4 style={styles.musicTitle}>{music.title}</h4>
                    <p style={styles.musicAuthor}>
                        <UserCircle size={14} style={{ verticalAlign: 'middle', marginRight: '4px', opacity: 0.8 }}/>
                        {music.author}
                    </p>
                </div>
            </div>
        );
    };

    // --- Main Render ---
    return (
        <div style={styles.pageContainer}>
            {/* Use Navbar with dark theme styles built-in */}
            <Navbar title="Lista de Músicas" showLogo={false} />

            {/* Content Area */}
            <div style={styles.contentArea}>
                {loading ? (
                    <div style={styles.centeredMessage}>
                        <div style={styles.spinner}></div>
                        <p>Carregando lista...</p>
                    </div>
                ) : error ? (
                    <div style={styles.centeredMessage}>
                        <AlertTriangle size={40} color="#ff6060" />
                        <p style={styles.errorText}>{error}</p>
                    </div>
                ) : musics.length === 0 ? (
                    <div style={styles.centeredMessage}>
                        <ListMusic size={40} color="#a0a0b0" />
                        <p style={{ color: "#a0a0b0", marginTop: '10px' }}>Nenhuma música na fila no momento.</p>
                        <p style={{ color: "#777", fontSize: '14px' }}>Use a busca para adicionar músicas!</p>
                    </div>
                ) : (
                    // Render the list of music items
                    musics.map(renderMusicItem)
                )}
            </div>

            {/* Footerbar replaces Sidebar */}
            <Footerbar />
        </div>
    );
}


// --- Styles ---
const styles = {
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#12121f',
        color: '#f0f0f0',
        overflowX: 'hidden',
        boxSizing: 'border-box',
    },
    contentArea: {
        flexGrow: 1,
        paddingTop: '70px', // Space below Navbar (60px height + 10px)
        paddingBottom: '75px', // Space above Footerbar (65px height + 10px)
        paddingLeft: '10px',
        paddingRight: '10px',
        overflowY: 'auto', // Allow content scrolling
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '700px', // Max width for the list content
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    centeredMessage: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 20px',
        color: '#a0a0b0',
        minHeight: 'calc(100vh - 60px - 65px - 10px)', // Adjust calculation based on actual nav/footer heights
        boxSizing: 'border-box',
    },
    errorText: {
        color: '#ff6060',
        marginTop: '10px',
        maxWidth: '80%',
        lineHeight: '1.4',
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
    },
    spinner: { // Consistent spinner style
        border: '4px solid rgba(255, 255, 255, 0.3)',
        borderLeftColor: '#a0a0ff',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        animation: 'spin 1s linear infinite',
        marginBottom: '15px',
    },

    // --- Music Item Styles ---
    musicCard: (isPlaying) => ({ // Function to apply conditional style
        display: "flex",
        alignItems: "center", // Vertically center items in the row
        // Use a slightly different background or border if playing
        backgroundColor: isPlaying ? "#2a2a4e" : "#1a1a2e", // Example: darker purple if playing
        border: isPlaying ? '1px solid #a0a0ff' : '1px solid #2a2a4e', // Border highlight if playing
        padding: "10px", // Adjust padding
        borderRadius: "8px",
        marginBottom: "12px", // Space between items
        boxShadow: "0 2px 5px rgba(0,0,0,0.3)", // Adjusted shadow
        transition: 'background-color 0.3s ease, border 0.3s ease', // Smooth transition
        boxSizing: 'border-box',
    }),
    thumbnail: {
        width: "80px", // Smaller fixed width thumbnail
        height: "60px", // Corresponding 4:3 height
        borderRadius: "6px",
        marginRight: "15px",
        objectFit: "cover",
        flexShrink: 0, // Prevent image from shrinking
        backgroundColor: '#333', // Placeholder bg
    },
    musicInfo: {
        flex: 1, // Take remaining space
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center', // Center text vertically if needed
        minWidth: 0, // Allow text area to shrink
    },
    badgesContainer: {
        display: 'flex',
        gap: '6px',
        marginBottom: '5px', // Space below badges
        height: '18px', // Fixed height for alignment
        alignItems: 'center',
    },
    badge: {
        display: 'inline-flex', // Use inline-flex to align icon and text
        alignItems: 'center',
        padding: "2px 6px",
        borderRadius: "4px",
        fontSize: "10px", // Smaller badge text
        fontWeight: '600', // Bolder badge text
        textTransform: 'uppercase',
        lineHeight: '1', // Ensure text fits vertically
    },
    playingBadge: {
        backgroundColor: "#a0a0ff", // Use theme purple
        color: "#12121f", // Dark text for contrast
    },
    myMusicBadge: {
        backgroundColor: "#FFD700", // Gold color for "My Music"
        color: "#333", // Dark text
    },
    musicTitle: {
        margin: "0 0 3px 0", // Reduced margin
        fontSize: "15px", // Slightly larger title
        fontWeight: "600",
        color: "#ffffff", // White text
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        // --- Limit title to 1 line ---
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        // --- OR Limit title to 2 lines (choose one) ---
        // display: '-webkit-box',
        // WebkitLineClamp: 2,
        // WebkitBoxOrient: 'vertical',
        // overflow: 'hidden',
        // textOverflow: 'ellipsis',
        // lineHeight: '1.3',
    },
    musicAuthor: {
        margin: 0,
        fontSize: "13px",
        color: "#a0a0b0", // Lighter gray text
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap', // Prevent wrapping
        overflow: 'hidden', // Hide overflow
        textOverflow: 'ellipsis', // Add ellipsis (...)
    },
}; // --- End Styles ---